import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { GlowWrapper } from "@/components/ui/glow-wrapper";

export function HeroSection() {
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, sectionId: string) => {
    e.preventDefault();
    const section = document.querySelector(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="hero" className="min-h-screen flex items-center relative overflow-hidden pt-20 bg-gradient-to-r from-teal-100 to-purple-100 dark:bg-gradient-to-r dark:from-teal-900/20 dark:to-purple-900/20">
      <div className="container mx-auto px-6 py-16 relative z-10">
        <motion.div 
          className="max-w-4xl mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-black dark:text-white text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Clarity in crisis. <span className="text-teal-600 dark:text-teal-400">Confidence in care.</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-200 mb-8 leading-relaxed">
            Elevita AI: Your Intelligent, Compassionate Digital Co-pilot for Every Stage of the Healthcare Journey in Australia.
          </p>
          
          <div 
            className="p-4 mb-8 max-w-3xl mx-auto rounded-lg bg-white/80 dark:bg-black/80"
          >
            <div className="flex items-center">
              <span className="text-green-400 mr-2" style={{ textShadow: '0 0 10px rgba(16, 185, 129, 0.5)' }}>🟢</span>
              <p className="text-sm text-gray-900 dark:text-green-100">
                <strong>Curalysis is LIVE in closed beta now.</strong> Organisation dashboards in development with Motor Neurone Disease Association of South Australia (MNDA SA).
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <GlowWrapper color="teal" intensity="high">
                <Button
                  asChild
                  variant="default"
                  size="lg"
                  className="text-black font-semibold"
                  style={{
                    background: 'linear-gradient(135deg, #14B8A6, #0891B2)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                  }}
                >
                  <a 
                    href="https://curalysis.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Join Curalysis Beta →
                  </a>
                </Button>
              </GlowWrapper>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                asChild
                variant="default"
                size="lg"
                className="text-black"
              >
                <a 
                  href="#roadmap" 
                  onClick={(e) => scrollToSection(e, "#roadmap")}
                >
                  Explore Our Specialist Solutions
                </a>
              </Button>
            </motion.div>
          </div>
          
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Transforming healthcare through lived experience innovation.
          </p>
        </motion.div>
        
        {/* Heartbeat line animation */}
        <motion.div 
          className="mt-16 relative max-w-4xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 0.5, duration: 1 }}
        >
          <div className="w-full h-24 flex items-center justify-center">
            <svg viewBox="0 0 1200 200" className="w-full h-full">
              <motion.path 
                d="M0,100 L300,100 L340,30 L380,170 L420,100 L800,100 L840,60 L880,140 L920,100 L1200,100" 
                fill="none" 
                stroke="#00C2CB" 
                strokeWidth="2"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 1 }}
                transition={{ duration: 3, repeat: Infinity, repeatType: "loop" }}
              />
            </svg>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
